import { useState, useTransition, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import './components/LoginForm.css';
import { useResetPasswordMutation } from './api/authApi';
import Eye from '../../assets/icons/Eye';

const FIELDS = [
  { name: 'newPassword',     label: 'New Password',         placeholder: 'Enter new password (5–20 characters)' },
  { name: 'confirmPassword', label: 'Confirm New Password', placeholder: 'Re-enter new password' },
];

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState({ newPassword: false, confirmPassword: false });
  const [successMsg, setSuccessMsg] = useState('');
  const [isPending, startTransition] = useTransition();
  const [resetPasswordApi] = useResetPasswordMutation();

  // Redirect if no token in URL
  useEffect(() => {
    if (!token) navigate('/login', { replace: true });
  }, [token, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const toggleShow = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.newPassword) {
      errors.newPassword = 'New Password is required';
    } else if (formData.newPassword.length < 5) {
      errors.newPassword = 'New Password must be at least 5 characters';
    } else if (formData.newPassword.length > 20) {
      errors.newPassword = 'New Password must not exceed 20 characters';
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirm Password is required';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Confirm Password does not match New Password';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    startTransition(async () => {
      try {
        const response = await resetPasswordApi({
          token,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword
        }).unwrap();

        if (response.success) {
          setSuccessMsg(response.message);
        }
      } catch (err) {
        const msg = err?.data?.message || err?.message || 'Failed to reset password. Please try again.';
        setFieldErrors({ form: msg });
      }
    });
  };

  if (!token) return null;

  return (
    <div className="container">
      <div className="card">
        <h1 className="title">Reset Password</h1>
        <p style={{ textAlign: 'center', color: '#4a5568', fontSize: '0.9rem', margin: '0.5rem 0 1.25rem' }}>
          Enter your new password below.
        </p>

        {successMsg ? (
          <>
            <div style={{
              background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px',
              padding: '0.875rem 1rem', color: '#276749', fontSize: '0.875rem', marginBottom: '1.25rem'
            }}>
              {successMsg}
            </div>
            <div style={{ textAlign: 'center' }}>
              <Link to="/login" className="link" style={{ fontSize: '0.9rem' }}>
                Go to Login →
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="form">
            {fieldErrors.form && <p className="error">{fieldErrors.form}</p>}

            {FIELDS.map(({ name, label, placeholder }) => (
              <div className="formGroup" key={name}>
                <label htmlFor={name} className="label">{label}</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type={showPassword[name] ? 'text' : 'password'}
                    id={name}
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    className="input"
                    placeholder={placeholder}
                    disabled={isPending}
                    style={{
                      paddingRight: '2.5rem',
                      borderColor: fieldErrors[name] ? '#e53e3e' : undefined,
                      boxShadow: fieldErrors[name] ? '0 0 0 1px #e53e3e' : undefined
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => toggleShow(name)}
                    tabIndex={-1}
                    aria-label={showPassword[name] ? `Hide ${label}` : `Show ${label}`}
                    style={{
                      position: 'absolute', right: '10px', background: 'none',
                      border: 'none', padding: 0, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', color: '#718096'
                    }}
                  >
                    <Eye size={18} color="#718096" />
                    {showPassword[name] && (
                      <span style={{
                        position: 'absolute', right: '8px', width: '2px', height: '20px',
                        background: '#718096', transform: 'rotate(45deg)', borderRadius: '1px',
                        pointerEvents: 'none'
                      }} />
                    )}
                  </button>
                </div>
                {fieldErrors[name] && (
                  <span style={{ fontSize: '0.78rem', color: '#e53e3e', marginTop: '2px' }}>
                    {fieldErrors[name]}
                  </span>
                )}
              </div>
            ))}

            <button type="submit" className="button" disabled={isPending}>
              {isPending ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        {!successMsg && (
          <div className="footer">
            <Link to="/login" className="link" style={{ fontSize: '0.875rem' }}>
              ← Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
